// src/components/resources/tools/generators/ColorPalette/ColorPalettePreview.tsx
'use client'

import { useCallback, useMemo } from 'react'
import { ClipboardIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline'
import { colorPaletteGenerator } from '~/lib/resources/data/generators/colorPaletteGenerator'
import type { GeneratorField } from '~/lib/resources/types/tools/generator'
import type { HarmonyType, WcagLevel } from '~/lib/utils/colors'
import { getString, getNumber, getBoolean, getStringArray } from '~/lib/utils/form-values'
import type { ColorInfo } from './types'

/**
 * Preview component that shows a live preview of the color palette
 * @param formData - The current form data
 * @param formatText - Function to format text with proper capitalization
 */
export function ColorPalettePreview({
  formData,
  formatText,
  onCopyColor,
  copiedColor,
  getFieldValue,
  fields: _fields,
  generatedColors,
}: {
  formData: Record<string, unknown>
  formatText: (text: string) => string
  onCopyColor: (color: string) => void
  copiedColor: string | null
  getFieldValue: (field: GeneratorField) => unknown
  fields: GeneratorField[]
  generatedColors: ColorInfo[]
}): JSX.Element {
  // Instead of using only the current step's fields, always use the full generator config for preview
  // This ensures all values (including those from previous steps) are available for preview
  const allFields = colorPaletteGenerator.config.steps.flatMap(step => step.fields)

  // Use getFieldValue to get effective values including defaults
  const getEffectiveValue = useCallback((fieldId: string, defaultValue: unknown = '') => {
    const field = allFields.find(f => f.id === fieldId)
    if (field) {
      return getFieldValue(field) ?? defaultValue
    }
    return formData[fieldId] ?? defaultValue
  }, [allFields, getFieldValue, formData])

  const colorName = getString(getEffectiveValue('colorName'), 'My Palette')
  const harmonyType = getString(getEffectiveValue('harmonyType'), 'monochromatic') as HarmonyType
  const paletteSize = getNumber(getEffectiveValue('paletteSize'), 5)
  const accessibilityLevel = getString(getEffectiveValue('accessibilityLevel'), 'aa') as WcagLevel
  const usageType = getStringArray(getEffectiveValue('usageType'))
  const includeNeutrals = getBoolean(getEffectiveValue('includeNeutrals'))
  // FIX: includeVariations logic (checked = include)
  const includeVariations = getBoolean(getEffectiveValue('includeVariations'))
  const saturationPref = getString(getEffectiveValue('saturation'), 'balanced')
  const lightnessPref = getString(getEffectiveValue('lightness'), 'medium')

  /**
   * Use the generated colors from the main palette generation
   *
   * CODE DEDUPLICATION: This replaces the previous duplicate color generation
   * logic in the preview component. Now the preview simply uses the colors
   * generated by the main generation function, ensuring consistency and
   * eliminating code duplication.
   */
  const sampleColors = useMemo(() => {
    return generatedColors.map(colorInfo => ({ color: colorInfo.color, name: colorInfo.name }))
  }, [generatedColors])

  return (
    <div className="space-y-4">
      {/* Palette Name */}
      <div>
        <h4 className="font-medium text-gray-900 dark:text-white">{colorName}</h4>
        {/* Using formatText to properly capitalize color harmony types like "Monochromatic" */}
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {formatText(harmonyType)} harmony
        </p>
      </div>

      {/* Color Swatch Grid */}
      <div className="grid grid-cols-1 gap-3">
        {sampleColors.map(({ color, name }, index) => (
          <div key={index} className="group">
            <div
              className="w-full h-16 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-between p-3 cursor-pointer transition-transform hover:scale-105"
              style={{ backgroundColor: color }}
              onClick={() => onCopyColor(color)}
            >
              <span className="text-xs px-2 py-1 bg-black bg-opacity-50 rounded text-white font-mono">
                {color}
              </span>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                {copiedColor === color ? (
                  <ClipboardDocumentCheckIcon className="h-4 w-4 text-white" />
                ) : (
                  <ClipboardIcon className="h-4 w-4 text-white" />
                )}
              </div>
            </div>
            {/* Use the color's ROLE name (not its array position) so an
                HSL-sorted palette shows the correct caption per swatch. */}
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {name ?? `Color ${index + 1}`}
            </p>
          </div>
        ))}
      </div>

      {/* Palette Specifications */}
      <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1 pt-2 border-t border-gray-200 dark:border-gray-600">
        <div><span className="font-medium">Size:</span> {paletteSize + (includeNeutrals ? 3 : 0)} colors</div>
        <div><span className="font-medium">Accessibility:</span> {formatText(`wcag ${accessibilityLevel}`)}</div>
        <div><span className="font-medium">Usage:</span> {usageType.join(', ') || 'General'}</div>
        {/* Using formatText to properly capitalize saturation and lightness values */}
        <div><span className="font-medium">Style:</span> {formatText(saturationPref)} • {formatText(lightnessPref)}</div>
        {includeNeutrals && <div className="text-gray-500">+ Neutral colors</div>}
        {includeVariations && <div className="text-gray-500">+ Variations</div>}
      </div>
    </div>
  )
}
